import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, Pagination, NoSearchResults, TABLE_MAXH_TABS } from "@/components/molecules";
import { useOperator, usePagination, useProfile } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { clientsPageSelector } from "@/redux/slices/client/selectors";
import { Client } from "@/redux/slices/client/types";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";
import { clsx } from "@/utils";
import { ClientsEmptyState } from "./components/ClientsEmptyState";
import { useClientsContext } from "./context";
import { ClientRowHandlers, generateClientColumns, generateClientData, getClientBookingPath } from "./helpers";
import { useGetClientsParams } from "./useGetClientsParams";

interface ClientsTableProps extends React.HTMLAttributes<HTMLDivElement> {
  items: Client[];
}

export const ClientsTable = ({ items, className, ...props }: ClientsTableProps) => {
  const { dateFormat } = useProfile();
  const { id: operatorId } = useOperator();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setStep, setSelectedClient } = useClientsContext();
  const { sorting, setSorting, search, filters, tab } = useGetClientsParams();
  const isFiltersEmpty = Object.values(filters).every((el) => el === undefined);

  // Each row action opens a confirm modal (see ClientsModals); the mutation fires there.
  const openClientStep = (client: Client, step: "remove-access" | "reactivate" | "resend-invite" | "cancel-invite") => {
    setSelectedClient(client);
    setStep(step);
  };

  const handlers: ClientRowHandlers = {
    onRemoveAccess: (client) => openClientStep(client, "remove-access"),
    onReactivate: (client) => openClientStep(client, "reactivate"),
    onResendInvite: (client) => openClientStep(client, "resend-invite"),
    onCancelInvite: (client) => openClientStep(client, "cancel-invite"),
    // Clear any in-flight draft first so a previous (possibly abandoned) attempt's locations/passenger
    // don't leak into this client's booking, then route to the OBO sheet tagged with the profile origin.
    onBookTrip: (client) => {
      dispatch(clearBooking());
      navigate(getClientBookingPath(operatorId, client.id), { state: { bookingOrigin: "client-profile" } });
    },
  };

  const columns = useMemo(() => generateClientColumns(tab), [tab]);
  const data = useMemo(
    () => generateClientData(items, dateFormat, operatorId, handlers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const renderEmptyState = () => {
    if (search) return <NoSearchResults />;
    if (!isFiltersEmpty) return <ClientsEmptyState hasFilters />;

    return <ClientsEmptyState tab={tab} onInvite={() => setStep("invite")} />;
  };

  return (
    <div className={clsx("relative flex bg-white", className)}>
      <DataTable
        className="w-full [&_table]:table-auto [&_tr]:bg-white"
        maxHeight={TABLE_MAXH_TABS}
        columns={columns}
        data={data}
        sorting={sorting}
        setSorting={setSorting}
        emptyState={renderEmptyState()}
        {...props}
      />
    </div>
  );
};

const ClientsTablePagination = () => {
  const { current, count, total, size } = useAppSelector(clientsPageSelector);
  const { handlePageClick, handleSizeChange } = usePagination("clients");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

ClientsTable.Pagination = ClientsTablePagination;
